import React from 'react';
import DetailsCard from '@common/components/General/DetailsCard';
import { useTranslation } from 'react-i18next';
import Mission from '../../models/missions/mission';
import MissionRun from '../../models/missions/mission-run';
import { DateTime } from 'luxon';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  BatteryEndIcon,
  BatteryStartIcon,
  CleanedAreaIcon,
  DownArrow,
  DurationIcon,
  PerformanceIcon,
  StartReasonIcon,
  StopReasonIcon,
} from '@common/utils/icons/icons';
import StatusIcon from './StatusIcon';
import MissionEndStatus from '../../models/enums/mission-status';
import useCustomLocalizationEffect from '../../hooks/useCustomLocalizationEffect';
import CustomLocalizationType from '../../models/enums/custom-localization-type';
import TextIcon from '../misc/TextIcon';
import { convertSecondsToRoundedString, dateToStringFormat } from '@common/utils/date';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomLabelTextTable from '../misc/CustomLabelTextTable';
import { TimeIcon } from '@mui/x-date-pickers';
import DistanceUnitSet from '@common/components/misc/DistanceUnitSet';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const MissionRuns: React.FC<{ mission: Mission }> = ({ mission }) => {
  const { t } = useTranslation();
  const localize = useCustomLocalizationEffect();
  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const runPerformance = (run: MissionRun) => {
    const durationMs = DateTime.fromISO(run.cleaningEnd).toMillis() - DateTime.fromISO(run.cleaningStart).toMillis();
    return durationMs > 0 ? (run.cleanedArea * 1000 * 3600) / durationMs : 0;
  };

  return (
    <DetailsCard cardTitle={t('title-runs')} enableContentHiding defaultHidden>
      <Stack sx={{ py: 1, width: '100%', overflow: 'hidden' }}>
        {[...mission.runs]
          .sort(
            (a, b) =>
              DateTime.fromISO(a.cleaningStart).toMillis() -
              DateTime.fromISO(b.cleaningStart).toMillis(),
          )
          .map((run) => (
            <Accordion key={run.id} sx={{ boxShadow: 1 }}>
              <AccordionSummary expandIcon={<DownArrow />}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  pr={1}
                  spacing={1}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <StatusIcon
                      status={
                        run.errors.length === 0
                          ? MissionEndStatus.SUCCEEDED
                          : run.errors.some((e) => e.errorType != null)
                            ? MissionEndStatus.FAILED
                            : MissionEndStatus.WARNING
                      }
                    />
                    <Typography sx={{ display: { xs: 'none', sm: 'inherit' } }}>
                      {run.zoneName != null && run.zoneName.length > 0 && `${run.zoneName} - `}
                      {localize(run.cleaningMode, CustomLocalizationType.CLEANING_MODE)}
                    </Typography>
                  </Stack>
                  {matchesSmDown ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {[
                        convertSecondsToRoundedString(
                          DateTime.fromJSDate(new Date(run.cleaningEnd)).toSeconds() -
                          DateTime.fromJSDate(new Date(run.cleaningStart)).toSeconds(),
                        ),
                        run.isCleaningRun && (
                          <DistanceUnitSet value={run.cleanedArea} round valueUnit={DistanceValueUnit.METERS_SQUARED} />
                        ),
                        run.isCleaningRun && (
                          <DistanceUnitSet value={runPerformance(run)} valueUnit={DistanceValueUnit.METERS_SQUARED_PER_HOUR} round />
                        ),
                      ]
                        .filter((part) => part !== false)
                        .map((part, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {part}
                            </Typography>
                          </React.Fragment>
                        ))}
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} flexWrap="nowrap">
                      <TextIcon
                        text={convertSecondsToRoundedString(
                          DateTime.fromJSDate(new Date(run.cleaningEnd)).toSeconds() -
                          DateTime.fromJSDate(new Date(run.cleaningStart)).toSeconds(),
                        )}
                        icon={<DurationIcon fontSize="small" />}
                        stackProps={{ sx: (theme) => ({ minWidth: theme.spacing(12), justifyContent: 'end' }) }}
                      />
                      {run.isCleaningRun && (
                        <React.Fragment>
                          <TextIcon
                            text={<DistanceUnitSet value={run.cleanedArea} round valueUnit={DistanceValueUnit.METERS_SQUARED} />}
                            icon={<CleanedAreaIcon fontSize="small" />}
                            stackProps={{ sx: (theme) => ({ minWidth: theme.spacing(12), justifyContent: 'end' }) }}
                          />
                          <TextIcon
                            text={
                              <DistanceUnitSet value={runPerformance(run)} valueUnit={DistanceValueUnit.METERS_SQUARED_PER_HOUR} round />
                            }
                            icon={<PerformanceIcon fontSize="small" />}
                            stackProps={{ sx: (theme) => ({ minWidth: theme.spacing(14), justifyContent: 'end' }) }}
                          />
                        </React.Fragment>
                      )}
                    </Stack>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="h2" sx={{ display: { sm: 'none', xs: 'inherit' } }}>
                      {run.zoneName != null && run.zoneName.length > 0 && `${run.zoneName} - `}
                      {localize(run.cleaningMode, CustomLocalizationType.CLEANING_MODE)}
                    </Typography>
                    {run.errors.map((error, index) => (
                      <CustomAlert
                        key={index}
                        sx={{ '.MuiAlert-message': { width: '100%' } }}
                        header={
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            width="100%"
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={0.5}
                          >
                            <Typography>
                              {error.errorType != null
                                ? localize(error.errorType, CustomLocalizationType.ERROR_CODE)
                                : localize(
                                  error.maintenanceType,
                                  CustomLocalizationType.MAINTENANCE_CODE,
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {dateToStringFormat(error.timestamp, false, mission.timezone)}
                            </Typography>
                          </Stack>
                        }
                        severity={error.errorType != null ? 'error' : 'warning'}
                        textMessages={(() => {
                          const message =
                            error.errorType != null
                              ? localize(error.errorType, CustomLocalizationType.ERROR_CODE, true)
                              : localize(
                                error.maintenanceType,
                                CustomLocalizationType.MAINTENANCE_CODE,
                                true,
                              );
                          return message != null ? [message] : undefined;
                        })()}
                      />
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomLabelTextTable
                      excludeNullRows
                      rows={[
                        {
                          startIcon: <StartReasonIcon fontSize="small" />,
                          label: t('table-header-mission-start-reason'),
                          text: localize(
                            run.startReason,
                            CustomLocalizationType.MISSION_START_STOP_REASON,
                          ),
                        },
                        {
                          startIcon: <TimeIcon fontSize="small" />,
                          label: t('table-header-mission-start-time'),
                          text: dateToStringFormat(run.cleaningStart, false, mission.timezone),
                        },
                        {
                          startIcon: <BatteryStartIcon fontSize="small" />,
                          label: t('table-header-run-battery-start'),
                          text: `${run.batteryPercentageStart} %`,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomLabelTextTable
                      excludeNullRows
                      rows={[
                        {
                          startIcon: <StopReasonIcon fontSize="small" />,
                          label: t('table-header-mission-stop-reason'),
                          text: localize(
                            run.stopReason,
                            CustomLocalizationType.MISSION_START_STOP_REASON,
                          ),
                        },
                        {
                          startIcon: <TimeIcon fontSize="small" />,
                          label: t('table-header-mission-end-time'),
                          text: dateToStringFormat(run.cleaningEnd, false, mission.timezone),
                        },
                        {
                          startIcon: <BatteryEndIcon fontSize="small" />,
                          label: t('table-header-run-battery-end'),
                          text: `${run.batteryPercentageEnd} %`,
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
      </Stack>
    </DetailsCard>
  );
};

export default MissionRuns;
